import type { IntlShape } from 'react-intl';

/**
 * The active `IntlShape`, published by `I18nProvider` so that code running
 * OUTSIDE React can translate.
 *
 * The callers here are redux-saga generators — `addresses/core/sagas.ts` and
 * `subscriptions/core/sagas.ts` — which build toast copy in response to failed
 * requests. A saga is not a component, so it can never call `useIntl()`, and
 * threading `intl` in through every dispatched action would mean every caller of
 * every action carried it.
 *
 * This is not the general answer whenever a hook is inconvenient. Where the call
 * site is inside React, pass `intl` explicitly — `getSubscriptionResume(intl)` and
 * `usePossessiveName` both do, because an explicit dependency beats a global.
 *
 * `activeIntl` is null until the provider has rendered once. Callers must handle
 * that rather than assume, which is why it falls back to the supplied text.
 *
 * Ported from petcon's src/shared/i18n/intlInstance.ts, where the same mechanism
 * serves its legacy DOM script.
 */
let activeIntl: IntlShape | null = null;

export const setActiveIntl = (intl: IntlShape) => {
  activeIntl = intl;
};

/**
 * Translates `id`, returning `fallback` when the provider has not published an
 * intl yet. Non-React callers get a plain string, never a React node.
 */
export const translateOutsideReact = (
  id: string,
  fallback: string,
  values?: Record<string, string | number>,
): string => {
  if (!activeIntl) return fallback;
  return activeIntl.formatMessage({ id, defaultMessage: fallback }, values);
};
